import { fetchStoreSpecials } from '@iheartjane/data-access';
import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import { FLAGS } from '@iheartjane/feature-flags';
import type { FlagsType } from '@iheartjane/feature-flags';
import type { QueryClient } from '@tanstack/react-query';
import type { LoaderFunctionArgs } from 'react-router';

import { ensureNumericId } from '@jane/kiosk/util';
import { getSpecialsQueryOptions } from '@jane/shared-ecomm/data-access';
import type { StoreSpecial } from '@jane/shared/types';

export type SpecialLoaderType = {
  special: StoreSpecial | V3StoreSpecial;
  specialId: number;
};

export const specialLoader =
  (queryClient: QueryClient, flags: FlagsType) =>
  async ({ params }: LoaderFunctionArgs): Promise<SpecialLoaderType> => {
    const { specialId, storeId } = params;
    const numericStoreId = ensureNumericId(storeId);
    const numericSpecialId = ensureNumericId(specialId);
    const useV3 = !!flags[FLAGS.c3UseV3StoreSpecials];

    if (useV3) {
      const response = await fetchStoreSpecials({
        appMode: 'kiosk',
        includeHidden: true,
        perPage: 1000,
        storeId: numericStoreId,
      });
      const matchingSpecial = response.specials.find(
        ({ id }) => id === numericSpecialId
      );

      if (!matchingSpecial) {
        throw new Error(
          `[specialLoader]: Special ${numericSpecialId} not detected in store's specials.`
        );
      }

      return {
        special: matchingSpecial,
        specialId: numericSpecialId,
      };
    }

    const specials = await queryClient.query({
      staleTime: 'static',
      ...getSpecialsQueryOptions(numericStoreId),
    });
    const matchingSpecial = specials.find(({ id }) => id === numericSpecialId);

    if (!matchingSpecial) {
      throw new Error(
        `[specialLoader]: Special ${numericSpecialId} not detected in store's specials.`
      );
    }

    return { special: matchingSpecial, specialId: numericSpecialId };
  };
